import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Btn } from "@/components/common";
import { X, ShieldCheck, Copy } from "lucide-react";
import { toast } from "sonner";

export function MfaSetupModal({ open, setOpen, enabled, onChange }) {
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const begin = async () => {
    try { const { data } = await api.post("/auth/mfa/setup"); setSecret(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post("/auth/mfa/verify", { code }); toast.success("MFA enabled"); setSecret(null); setCode(""); onChange?.(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const disable = async () => {
    if (!window.confirm("Disable MFA for your account?")) return;
    try { await api.post("/auth/mfa/disable"); toast.success("MFA disabled"); onChange?.(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setOpen(false)}>
      <div data-testid="mfa-modal" className="card-surface rounded-xl p-5 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-slate-100 flex items-center gap-2"><ShieldCheck size={18} className="text-cyan-400" />Multi-Factor Authentication</h2>
          <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-200"><X size={18} /></button>
        </div>
        {enabled ? (
          <div>
            <p className="text-[13px] text-emerald-300 mb-4">MFA is currently enabled on your account.</p>
            <Btn variant="danger" data-testid="mfa-disable-btn" onClick={disable}>Disable MFA</Btn>
          </div>
        ) : !secret ? (
          <div>
            <p className="text-[13px] text-slate-400 mb-4">Protect your account with a time-based one-time passcode (TOTP) app such as Google Authenticator, Microsoft Authenticator or 1Password.</p>
            <Btn data-testid="mfa-begin-btn" onClick={begin}>Begin setup</Btn>
          </div>
        ) : (
          <div>
            <p className="text-[13px] text-slate-400 mb-2">Add this secret key to your authenticator app, then enter the 6-digit code it shows.</p>
            <div className="flex items-center gap-2 mb-3">
              <code className="flex-1 bg-slate-950 border border-slate-800 rounded p-2 text-[13px] text-cyan-300 break-all font-mono">{secret.secret}</code>
              <button onClick={() => { navigator.clipboard.writeText(secret.secret); toast.success("Copied"); }} className="text-cyan-400 shrink-0"><Copy size={16} /></button>
            </div>
            <input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000"
              className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-slate-100 text-center text-lg tracking-[0.4em] font-mono mb-3" />
            <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setSecret(null)}>Back</Btn><Btn data-testid="mfa-verify-btn" onClick={verify} disabled={code.length !== 6 || busy}>Verify & Enable</Btn></div>
          </div>
        )}
      </div>
    </div>
  );
}
